/**
 * useWorkflowConstellation — Workflow-centered constellation with lens filtering.
 *
 * Queries the backend `constellationWithLens` endpoint to get a constellation
 * annotated with lens ring_filters (highlight/de-emphasize/show/hide).
 *
 * Definition mode (uppercase symbols): shows all possible connections for an entity TYPE.
 * Instance mode (lowercase symbols): shows actual connections for a specific entity RECORD.
 *
 * Produces nodes/edges compatible with `UniversalConstellationGraph` for visualization.
 *
 * DRR-090: Rules-first constellation architecture.
 * DRR-085: Constellations as config type #12.
 */

import { useMemo, useState, useCallback } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL Operation
// ---------------------------------------------------------------------------

const CONSTELLATION_WITH_LENS_QUERY = gql`
  query GetConstellationWithLens(
    $constellationId: String!
    $lensId: String!
    $mode: String!
  ) {
    constellationWithLens(
      constellationId: $constellationId
      lensId: $lensId
      mode: $mode
    ) {
      constellationId
      constellationName
      lensId
      lensName
      mode
      rings {
        ringId
        ringNumber
        label
        color
        shape
        provisioning
        onboardingAction
        highlighted
        deEmphasized
        showFields
        hideFields
        expandSections
        modeDescription
        instanceStatusFields
      }
      checklist {
        step
        ring
        statusField
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Raw GraphQL response shape for constellationWithLens query. */
interface ConstellationWithLensResponse {
  constellationId: string;
  constellationName: string;
  lensId: string;
  lensName: string;
  mode: string;
  rings: Array<{
    ringId: string;
    ringNumber: number;
    label: string;
    color?: string | null;
    shape?: string | null;
    provisioning?: string | null;
    onboardingAction?: string | null;
    highlighted: string[];
    deEmphasized: string[];
    showFields: string[];
    hideFields: string[];
    expandSections: string[];
    modeDescription?: string | null;
    instanceStatusFields: string[];
  }>;
  checklist: Array<{
    step: string;
    ring: string;
    statusField: string;
  }>;
}

/** Role a node plays in a workflow constellation. */
export type WorkflowConstellationRole =
  | 'center'    // The workflow/service plan at the hub
  | 'ring'      // A ring label node (participants, authorities, etc.)
  | 'config';   // An individual config item within a ring

/** A node in the workflow constellation. */
export interface WorkflowConstellationNode {
  /** Unique node ID. */
  nodeId: string;
  /** Display name. */
  name: string;
  /** Role in the constellation. */
  role: WorkflowConstellationRole;
  /** Ring number (0=center, 1-4=rings). */
  ringNumber: number;
  /** Ring ID this node belongs to. */
  ringId?: string;
  /** Hex color for visualization. */
  color?: string;
  /** Cytoscape.js node shape. */
  shape?: string;
  /** Whether this node is highlighted by the lens. */
  highlighted?: boolean;
  /** Whether this node is de-emphasized by the lens. */
  deEmphasized?: boolean;
  /** Provisioning status (draft/locked/etc). */
  provisioning?: string;
  /** Onboarding action instruction. */
  onboardingAction?: string;
  /** Mode-specific description. */
  modeDescription?: string;
}

/** An edge in the workflow constellation. */
export interface WorkflowConstellationEdge {
  /** Source node ID. */
  sourceId: string;
  /** Target node ID. */
  targetId: string;
  /** Edge type label. */
  edgeType: string;
  /** Human-readable label. */
  label: string;
}

/** Onboarding checklist step. */
export interface ChecklistStep {
  step: string;
  ring: string;
  statusField: string;
}

/** Filtered ring data from the backend. */
export interface FilteredRing {
  ringId: string;
  ringNumber: number;
  label: string;
  color?: string;
  shape?: string;
  provisioning?: string;
  onboardingAction?: string;
  highlighted: string[];
  deEmphasized: string[];
  showFields: string[];
  hideFields: string[];
  expandSections: string[];
  modeDescription?: string;
  instanceStatusFields: string[];
}

/** Full workflow constellation data. */
export interface WorkflowConstellationData {
  /** Central node (the constellation subject). */
  center: WorkflowConstellationNode;
  /** All nodes (center + ring labels + config items). */
  nodes: WorkflowConstellationNode[];
  /** All edges connecting center → rings → items. */
  edges: WorkflowConstellationEdge[];
  /** Filtered rings with lens annotations. */
  rings: FilteredRing[];
  /** Onboarding checklist from the lens. */
  checklist: ChecklistStep[];
  /** Active mode (definition/instance). */
  mode: string;
  /** Lens name. */
  lensName: string;
  /** Ring count. */
  ringCount: number;
  /** Total highlighted items across all rings. */
  highlightedCount: number;
}

// ---------------------------------------------------------------------------
// Props & Return
// ---------------------------------------------------------------------------

export interface UseWorkflowConstellationProps {
  /** Constellation config ID (e.g., "constellation-care-service-plan"). */
  constellationId: string;
  /** Lens config ID (e.g., "ln-5-0-001"). */
  lensId: string;
  /** Initial mode: "definition" (entity types) or "instance" (entity records). */
  initialMode?: 'definition' | 'instance';
}

export interface UseWorkflowConstellationReturn {
  /** Full constellation data for visualization. */
  constellation: WorkflowConstellationData | null;
  /** Loading state. */
  isLoading: boolean;
  /** Error state. */
  error: Error | null;
  /** Current mode. */
  mode: 'definition' | 'instance';
  /** Toggle between definition and instance mode. */
  toggleMode: () => void;
  /** Set mode explicitly. */
  setMode: (mode: 'definition' | 'instance') => void;
  /** Onboarding checklist. */
  checklist: ChecklistStep[];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useWorkflowConstellation(
  props: UseWorkflowConstellationProps,
): UseWorkflowConstellationReturn {
  const { constellationId, lensId, initialMode = 'definition' } = props;

  const [mode, setMode] = useState<'definition' | 'instance'>(initialMode);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'definition' ? 'instance' : 'definition'));
  }, []);

  // Apollo query
  const { data, loading, error } = useQuery<{ constellationWithLens: ConstellationWithLensResponse | null }>(CONSTELLATION_WITH_LENS_QUERY, {
    variables: { constellationId, lensId, mode },
    fetchPolicy: 'cache-and-network',
    skip: !constellationId || !lensId,
  });

  // Transform GraphQL response → constellation visualization data
  const constellation = useMemo<WorkflowConstellationData | null>(() => {
    const raw = data?.constellationWithLens;
    if (!raw) return null;

    const nodes: WorkflowConstellationNode[] = [];
    const edges: WorkflowConstellationEdge[] = [];

    // Center node
    const center: WorkflowConstellationNode = {
      nodeId: `center:${raw.constellationId}`,
      name: raw.constellationName,
      role: 'center',
      ringNumber: 0,
    };
    nodes.push(center);

    let highlightedCount = 0;

    // Build ring label nodes + highlighted/de-emphasized config item nodes
    for (const ring of raw.rings) {
      // Ring label node
      const ringNode: WorkflowConstellationNode = {
        nodeId: `ring:${ring.ringId}`,
        name: ring.label,
        role: 'ring',
        ringNumber: ring.ringNumber,
        ringId: ring.ringId,
        color: ring.color ?? undefined,
        shape: ring.shape ?? undefined,
        provisioning: ring.provisioning ?? undefined,
        onboardingAction: ring.onboardingAction ?? undefined,
        modeDescription: ring.modeDescription ?? undefined,
      };
      nodes.push(ringNode);

      // Edge from center → ring
      edges.push({
        sourceId: center.nodeId,
        targetId: ringNode.nodeId,
        edgeType: 'CONTAINS',
        label: ring.label,
      });

      // Highlighted config items within this ring
      for (const itemId of ring.highlighted) {
        highlightedCount += 1;
        const itemNode: WorkflowConstellationNode = {
          nodeId: `config:${itemId}`,
          name: itemId,
          role: 'config',
          ringNumber: ring.ringNumber,
          ringId: ring.ringId,
          color: ring.color ?? undefined,
          highlighted: true,
        };
        nodes.push(itemNode);
        edges.push({
          sourceId: ringNode.nodeId,
          targetId: itemNode.nodeId,
          edgeType: 'HIGHLIGHTS',
          label: 'highlight',
        });
      }

      // De-emphasized config items
      for (const itemId of ring.deEmphasized) {
        const itemNode: WorkflowConstellationNode = {
          nodeId: `config:${itemId}`,
          name: itemId,
          role: 'config',
          ringNumber: ring.ringNumber,
          ringId: ring.ringId,
          deEmphasized: true,
        };
        nodes.push(itemNode);
        edges.push({
          sourceId: ringNode.nodeId,
          targetId: itemNode.nodeId,
          edgeType: 'DE_EMPHASIZES',
          label: 'de-emphasize',
        });
      }
    }

    return {
      center,
      nodes,
      edges,
      rings: raw.rings.map((r) => ({
        ...r,
        color: r.color ?? undefined,
        shape: r.shape ?? undefined,
        provisioning: r.provisioning ?? undefined,
        onboardingAction: r.onboardingAction ?? undefined,
        modeDescription: r.modeDescription ?? undefined,
      })),
      checklist: raw.checklist ?? [],
      mode: raw.mode,
      lensName: raw.lensName,
      ringCount: raw.rings.length,
      highlightedCount,
    };
  }, [data]);

  return {
    constellation,
    isLoading: loading,
    error: error ?? null,
    mode,
    toggleMode,
    setMode,
    checklist: constellation?.checklist ?? [],
  };
}
